import {
	Button,
	ChipConfirmModal,
	ChipSelect,
	type ChipSelectOption,
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
	Input,
	ResourceOptions,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark, ChevronDown, Plus } from "lucide-react";
import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { rememberAnalysisContext } from "~/core/analysis-context";
import { ApiError, api, type SavedView } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { ENGINES } from "~/modules/analytics/lib/engines";
import type { Segment } from "~/modules/analytics/lib/loop";

export interface FilterState {
	range: string;
	engine: string;
	locale: string;
	/**
	 * The four the insight tabs add. Optional because every other screen in the
	 * panel predates them and reads none of them — a required field would put an
	 * empty string on eleven screens that would then have to strip it again.
	 */
	topics?: string;
	intent?: string;
	category?: string;
	tag?: string;
}

/** The prompt types, which are `questions.intent` under the name Profound uses. */
export const INTENTS = ["informational", "comparison", "alternatives", "commercial", "transactional"] as const;

/**
 * The engines the brand on screen is measured on.
 *
 * A context rather than a prop, because the engine filter appears on fifteen
 * screens and none of them has any other reason to know: threading it through
 * would be fifteen signatures changed to say one thing that never varies within
 * a brand.
 *
 * Empty means "we do not know yet" — the brand list has not loaded — and the
 * filter falls back to the whole catalogue rather than rendering a dropdown
 * with nothing in it.
 */
const BrandEngines = createContext<readonly string[]>([]);

export function BrandEnginesProvider({ engines, children }: { engines: string[]; children: React.ReactNode }) {
	return <BrandEngines.Provider value={engines}>{children}</BrandEngines.Provider>;
}

/**
 * The engine options for a filter bar, and a value that is one of them.
 *
 * Two things, because they are one decision. A brand that dropped Gemini last
 * week may still have `engine=gemini` stored in this browser, and a Radix select
 * whose value is not among its items renders blank — so the stored value is
 * cleared as the list narrows. Without that, somebody reads a screen filtered to
 * an engine the bar cannot show them and cannot switch off.
 */
function useEngineOptions(value: string, onChange: (next: Partial<FilterState>) => void) {
	const brand = useContext(BrandEngines);
	const options = brand.length > 0 ? ENGINES.filter((engine) => brand.includes(engine.key)) : ENGINES;
	const known = options.some((engine) => engine.key === value);

	useEffect(() => {
		if (value && brand.length > 0 && !known) onChange({ engine: "" });
	}, [value, brand.length, known, onChange]);

	return { options, value: known ? value : "all" };
}

/**
 * The scope, without the saved views.
 *
 * For the two screens that are not readings of a stage — the overview and the
 * composer. Same segments, same discipline, same rule: what is drawn is what
 * the screen honours.
 */
export function Filters({
	segments,
	value,
	onChange,
	children,
}: {
	segments: readonly Segment[];
	value: FilterState;
	onChange: (next: Partial<FilterState>) => void;
	children?: React.ReactNode;
}) {
	return (
		<ScopeBar tab="" segments={segments} views={false} value={value} onChange={onChange}>
			{children}
		</ScopeBar>
	);
}

function Filter({
	label,
	value,
	onChange,
	options,
}: {
	label: string;
	value: string;
	onChange: (value: string) => void;
	options: ChipSelectOption[];
}) {
	return (
		// The caption sits inside the trigger, so the whole segment — not just
		// the value — is the hit target, and reads to a screen reader as one
		// name: "engine, all engines".
		<ChipSelect
			value={value}
			onChange={onChange}
			aria-label={label}
			displayLabel={`${label}: ${options.find((option) => option.value === value)?.label ?? value}`}
			className="scope-trigger"
			align="start"
			options={options}
		/>
	);
}

// ---------------------------------------------------------------------------
// The scope bar
// ---------------------------------------------------------------------------

/**
 * The scope every reading in the loop is read through.
 *
 * One bar across all five stages, and which segments it draws is the reading's
 * own declaration in `LOOP` rather than a decision made here. The rule that
 * makes this worth a prop: a reading either honours a segment or the bar does
 * not draw it. A prompt-type filter on the citations tab would narrow the
 * numbers in a way the tab gives no way to see, and a segment the endpoint
 * drops at the edge is worse still — somebody sets it, believes the figures
 * under it, and reads the wrong one for a month.
 *
 * The comparison is a label rather than a menu. Every figure that carries one
 * compares against the window immediately before it, computed the same way from
 * the same rows, and that is the only comparison the server can honestly
 * produce — a dropdown offering "last year" against nine months of data would
 * be offering to invent one.
 */
export function ScopeBar({
	entityId,
	tab,
	segments,
	views = true,
	value,
	onChange,
	topics,
	tags,
	children,
}: {
	/** Absent on the two screens outside the loop, which save no views. */
	entityId?: string;
	/** Which reading a saved view should reopen on. */
	tab: string;
	/** What this reading honours, from `LOOP`. */
	segments: readonly Segment[];
	views?: boolean;
	value: FilterState;
	onChange: (next: Partial<FilterState>) => void;
	/** The topics that exist, from whichever reading loaded them. */
	topics?: string[];
	tags?: string[];
	children?: React.ReactNode;
}) {
	const t = useT();
	const engines = useEngineOptions(value.engine, onChange);
	const setEngine = (engine: string) => onChange({ engine: engine === "all" ? "" : engine });
	const picked = (value.topics ?? "").split(",").filter(Boolean);
	const has = (segment: Segment) => segments.includes(segment);
	useEffect(() => {
		if (!entityId) return;
		rememberAnalysisContext({
			brandId: entityId,
			reading: tab,
			filters: Object.fromEntries(segments.map((key) => [key, value[key] ?? ""])),
			capturedAt: new Date().toISOString(),
		});
	}, [entityId, tab, segments, value]);

	// The server narrows every reading by the picked topics, including the ones
	// that never load the topic list — so a pick carried over from Visibility
	// is drawn here, with its own entries, rather than narrowing the numbers
	// from behind a control that is not on the screen.
	const topicOptions = [...new Set([...(topics ?? []), ...picked])];
	const showTopics = (has("topics") || picked.length > 0) && topicOptions.length > 0;

	function toggleTopic(topic: string) {
		const next = picked.includes(topic) ? picked.filter((one) => one !== topic) : [...picked, topic];
		onChange({ topics: next.join(",") });
	}

	const summary = [
		has("range") ? t(`filter.range${value.range.replace("d", "")}`) : null,
		has("engine") ? t(engines.value !== "all" ? `engine.${engines.value}` : "filter.allEngines") : null,
		has("locale") ? t(`locale.${value.locale}`) : null,
		picked.length ? t("filter.nTopics", { n: picked.length }) : null,
		has("intent") && value.intent ? t(`questions.intent.${value.intent}`) : null,
		has("category") && value.category ? t(`citations.kind.${value.category}`) : null,
		has("tag") && value.tag ? value.tag : null,
	]
		.filter(Boolean)
		.join(" · ");
	return (
		<ResourceOptions
			translate={t}
			className="analytics-scope-tools border-b-0"
			aside={
				<span className="analytics-scope-summary">
					{summary}
					{has("range") ? <span className="filter-vs"> · {t("filter.vsPrevious")}</span> : null}
				</span>
			}
			filter={
				segments.length
					? {
							active: Boolean(
								(has("engine") && engines.value !== "all") ||
									(has("topics") && picked.length) ||
									(has("intent") && value.intent) ||
									(has("category") && value.category) ||
									(has("tag") && value.tag),
							),
							content: (
								<div className="analytics-filter-fields">
									{has("range") ? (
										<>
											<Filter
												label={t("filter.range")}
												value={value.range}
												onChange={(range) => onChange({ range })}
												options={[
													{ value: "7d", label: t("filter.range7") },
													{ value: "30d", label: t("filter.range30") },
													{ value: "90d", label: t("filter.range90") },
												]}
											/>
										</>
									) : null}

									{showTopics ? (
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="default" className="scope-trigger max-sm:w-full max-sm:justify-between">
													<span className="filter-label">{t("filter.topics")}</span>
													<span>
														{picked.length === 0 ? t("filter.allTopics") : t("filter.nTopics", { n: picked.length })}
													</span>
													<ChevronDown aria-hidden />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="start">
												<DropdownMenuLabel>{t("filter.topics")}</DropdownMenuLabel>
												{topicOptions.map((topic) => (
													<DropdownMenuCheckboxItem
														key={topic}
														checked={picked.includes(topic)}
														onCheckedChange={() => toggleTopic(topic)}
													>
														{topic || t("insights.noTopic")}
													</DropdownMenuCheckboxItem>
												))}
												{picked.length > 0 ? (
													<>
														<DropdownMenuSeparator />
														<DropdownMenuItem onSelect={() => onChange({ topics: "" })}>
															{t("filter.clear")}
														</DropdownMenuItem>
													</>
												) : null}
											</DropdownMenuContent>
										</DropdownMenu>
									) : null}

									{has("engine") ? (
										<Filter
											label={t("filter.engine")}
											value={engines.value}
											onChange={setEngine}
											options={[
												{ value: "all", label: t("filter.allEngines") },
												...engines.options.map((engine) => ({ value: engine.key, label: t(`engine.${engine.key}`) })),
											]}
										/>
									) : null}

									{has("intent") ? (
										<Filter
											label={t("filter.intent")}
											value={value.intent || "all"}
											onChange={(intent) => onChange({ intent: intent === "all" ? "" : intent })}
											options={[
												{ value: "all", label: t("filter.allIntents") },
												...INTENTS.map((intent) => ({ value: intent, label: t(`questions.intent.${intent}`) })),
											]}
										/>
									) : null}

									{has("category") ? (
										<Filter
											label={t("filter.category")}
											value={value.category || "all"}
											onChange={(category) => onChange({ category: category === "all" ? "" : category })}
											options={[
												{ value: "all", label: t("filter.allCategories") },
												...["yours", "ondit", "competitor", "directory", "community", "editorial", "other"].map(
													(kind) => ({
														value: kind,
														label: t(`citations.kind.${kind}`),
													}),
												),
											]}
										/>
									) : null}

									{has("tag") && tags && tags.length > 0 ? (
										<Filter
											label={t("filter.tag")}
											value={value.tag || "all"}
											onChange={(tag) => onChange({ tag: tag === "all" ? "" : tag })}
											options={[
												{ value: "all", label: t("filter.allTags") },
												...tags.map((tag) => ({ value: tag, label: tag })),
											]}
										/>
									) : null}

									{has("locale") ? (
										<Filter
											label={t("filter.locale")}
											value={value.locale}
											onChange={(locale) => onChange({ locale })}
											options={[
												{ value: "en", label: t("locale.en") },
												{ value: "zh", label: t("locale.zh") },
											]}
										/>
									) : null}
								</div>
							),
						}
					: undefined
			}
			asideEnd={
				views && entityId && segments.length > 0 ? (
					<SavedViews entityId={entityId} tab={tab} value={value} onChange={onChange} />
				) : null
			}
			trailing={children}
		/>
	);
}

/**
 * The filter bar, kept.
 *
 * Six filters across nine tabs is a combination somebody rebuilds every Monday,
 * and it is per person rather than per account: two people looking at the same
 * brand are usually looking at different slices of it. Saving is not a spend,
 * so a lapsed account can still do it — reading its own data comfortably is
 * exactly what a lapsed account may still do.
 */
function SavedViews({
	entityId,
	tab,
	value,
	onChange,
}: {
	entityId: string;
	tab: string;
	value: FilterState;
	onChange: (next: Partial<FilterState>) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const [naming, setNaming] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [removing, setRemoving] = useState<SavedView | null>(null);
	const [name, setName] = useState("");
	const [saving, setSaving] = useState(false);
	const queryClient = useQueryClient();
	const views = useQuery({
		queryKey: [...keys.entity(entityId), "views"],
		queryFn: () => api.views(entityId),
		...read,
	});
	const saveView = useMutation({
		mutationFn: (view: Parameters<typeof api.saveView>[1]) => api.saveView(entityId, view),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	const removeView = useMutation({
		mutationFn: (id: string) => api.removeView(entityId, id),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	async function save() {
		// Enter in the field and a click on the button both land here, and one
		// after the other is two views with one name.
		if (!name.trim() || saving) return;
		setSaving(true);
		try {
			await saveView.mutateAsync({
				name: name.trim(),
				tab,
				// Only the filters, never the whole state: a view is what to look
				// at, and a stored copy of anything else would come back stale.
				filters: {
					range: value.range,
					engine: value.engine,
					locale: value.locale,
					topics: value.topics ?? "",
					intent: value.intent ?? "",
					category: value.category ?? "",
					tag: value.tag ?? "",
				},
			});
			setName("");
			setNaming(false);
			toast.success(t("filter.viewSaved"));
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setSaving(false);
		}
	}

	async function remove(view: SavedView) {
		try {
			await removeView.mutateAsync(view.id);
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		}
	}

	const saved = views.data ?? [];

	return (
		<>
			<DropdownMenu
				open={naming || menuOpen}
				onOpenChange={(open) => {
					setMenuOpen(open);
					if (!open) setNaming(false);
				}}
			>
				<DropdownMenuTrigger asChild>
					<Button variant="default" className="scope-trigger max-sm:w-full max-sm:justify-between">
						<Bookmark aria-hidden />
						<span>{t("filter.views")}</span>
						<ChevronDown aria-hidden />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="start" className="min-w-64">
					<DropdownMenuLabel>{t("filter.views")}</DropdownMenuLabel>
					{saved.length === 0 ? (
						<DropdownMenuItem disabled>{t("filter.noViews")}</DropdownMenuItem>
					) : (
						saved.map((view) => (
							<DropdownMenuItem
								key={view.id}
								onSelect={() =>
									onChange({
										range: view.filters.range || value.range,
										engine: view.filters.engine ?? "",
										locale: view.filters.locale || value.locale,
										topics: view.filters.topics ?? "",
										intent: view.filters.intent ?? "",
										category: view.filters.category ?? "",
										tag: view.filters.tag ?? "",
									})
								}
							>
								<span className="flex-1">{view.name}</span>
							</DropdownMenuItem>
						))
					)}
					{saved.length ? (
						<DropdownMenuSub>
							<DropdownMenuSubTrigger>{t("filter.removeView")}</DropdownMenuSubTrigger>
							<DropdownMenuSubContent>
								{saved.map((view) => (
									<DropdownMenuItem
										key={view.id}
										className="text-[var(--text-error)]"
										onSelect={() => {
											setRemoving(view);
											setNaming(false);
											setMenuOpen(false);
										}}
									>
										{view.name}
									</DropdownMenuItem>
								))}
							</DropdownMenuSubContent>
						</DropdownMenuSub>
					) : null}
					<DropdownMenuSeparator />
					{naming ? (
						<div className="menu-form">
							<Input
								autoFocus
								value={name}
								maxLength={60}
								placeholder={t("filter.viewName")}
								onChange={(event) => setName(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Enter") save();
									if (event.key === "Escape") setNaming(false);
								}}
							/>
							<Button variant="primary" type="button" onClick={save} disabled={saving}>
								{t("common.save")}
							</Button>
						</div>
					) : (
						<DropdownMenuItem
							onSelect={(event) => {
								event.preventDefault();
								setNaming(true);
							}}
						>
							<Plus aria-hidden />
							{t("filter.saveView")}
						</DropdownMenuItem>
					)}
				</DropdownMenuContent>
			</DropdownMenu>
			<ChipConfirmModal
				open={!!removing}
				onOpenChange={(open) => {
					if (!open) setRemoving(null);
				}}
				title={`${t("common.remove")} · ${removing?.name ?? ""}`}
				text={t("common.removeConfirm")}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("common.remove"),
					variant: "destructive",
					onClick: () => {
						if (removing) void remove(removing);
						setRemoving(null);
					},
				}}
			/>
		</>
	);
}
